import { isSameApi } from '../api/api-url';
import type { HttpClient, RequestOptions } from '../api/http';
import { HttpFailure } from '../api/http-failure';
import { appConfig } from '../config/app-config';
import { noteOutcome } from './reach.store';

/**
 * 036 (issue 208) — the wrapper that turns an ordinary request's own outcome into what
 * `reach.store.ts` knows about the server (research R-005). It changes NOTHING about the request or
 * its result: `createObservingClient(inner).get(...)` resolves with exactly what `inner.get(...)`
 * resolves with, and rejects with exactly what it rejects with — the same property
 * `core/stored-views/storing-client.ts` and `renewing-client.ts` both keep for the requests they
 * pass through.
 *
 * **Composed OUTERMOST** (`main.tsx`), so it observes exactly what the rest of the application
 * sees: every request `appClient()` issues, after the offline guard and the renewal retry have
 * already had their say — not a raw `fetch` this wrapper would have to reason about timeouts and
 * retries for a second time.
 *
 * Requests to another origin are not observed — `isSameApi` (already the renewing wrapper's own
 * gate) decides that; a request this application makes to somewhere that is not its own API says
 * nothing about whether ITS server is reachable.
 *
 * A `2xx`/`3xx`/`4xx` all count as "reachable" here — `reach.store.ts`'s own rule cares only about
 * `status < 500` versus `status >= 500` versus no answer at all, never whether the BODY is the
 * answer the caller actually wanted (that is `core/outbox/outbox.store.ts`'s own, narrower, "is this
 * actually an answer" question for the one route it sends). `http.ts` never hands this wrapper the
 * exact status of an OK response — only the parsed body — so every successful resolution is
 * reported as `200`, which is indistinguishable from any other `status < 500` as far as
 * `noteOutcome` is concerned.
 *
 * The outbox's OWN sends never reach this wrapper — they go through a client built straight over
 * `directClient()` (`sendingClient` in `outbox.store.ts`), which is deliberate: that file reports
 * its own outcomes itself, tagged `{ ownSend: true }`, precisely so a row's own successful send is
 * never counted as the bad-row rule's evidence for a DIFFERENT row (`reach.store.ts`'s own comment
 * on `NoteOutcomeOptions.ownSend`).
 */
export function createObservingClient(inner: HttpClient): HttpClient {
  const wrap = <T,>(url: string, run: () => Promise<T>): Promise<T> => {
    if (!isSameApi(url, appConfig().apiBaseUrl)) return run();

    return run().then(
      (value) => {
        // `http.ts` returns the raw text of a body it could not parse as JSON, and `null` for none.
        noteOutcome(200, { notJson: typeof value === 'string' });
        return value;
      },
      (error: unknown) => {
        if (error instanceof HttpFailure) noteOutcome(error.status);
        throw error;
      },
    );
  };

  return {
    get: <T,>(url: string, options?: RequestOptions) => wrap(url, () => inner.get<T>(url, options)),
    post: <T,>(url: string, body: unknown, options?: RequestOptions) =>
      wrap(url, () => inner.post<T>(url, body, options)),
    put: <T,>(url: string, body: unknown, options?: RequestOptions) =>
      wrap(url, () => inner.put<T>(url, body, options)),
    patch: <T,>(url: string, body: unknown, options?: RequestOptions) =>
      wrap(url, () => inner.patch<T>(url, body, options)),
    delete: <T,>(url: string, options?: RequestOptions) => wrap(url, () => inner.delete<T>(url, options)),
  };
}
